import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import GradientBackground from '../components/GradientBackground';
import GlassCard from '../components/GlassCard';
import NavBar from '../components/NavBar';
import ClaimsDisplay from './ClaimsDisplay';
import { useAuth } from '../hooks/useAuth';
import type { IdTokenClaims } from '../auth/AuthTokenTypes';

export type ProfileClaimsViewModel = {
  displayName: string;
  email: string;
  avatarUrl?: string;
  issuer?: string;
  subject?: string;
  audience?: string;
  issuedAtIso?: string;
  expiresAtIso?: string;
  rawClaims: Record<string, unknown>;
};

export type CopyClaimsResult =
  | { ok: true }
  | {
      ok: false;
      reason: 'clipboard_unavailable' | 'permission_denied' | 'unknown';
      errorMessage?: string;
    };

export function buildProfileClaimsViewModel(
  claims: IdTokenClaims | null | undefined,
): ProfileClaimsViewModel {
  if (!claims) {
    return {
      displayName: 'Unknown User',
      email: '',
      rawClaims: {},
    };
  }

  const aud = Array.isArray(claims.aud) ? claims.aud.join(', ') : claims.aud;

  return {
    displayName:
      claims.name ??
      ([claims.given_name, claims.family_name].filter(Boolean).join(' ') || null) ??
      claims.email ??
      claims.sub,
    email: claims.email ?? '',
    avatarUrl: claims.picture,
    issuer: claims.iss,
    subject: claims.sub,
    audience: aud,
    issuedAtIso: claims.iat ? new Date(claims.iat * 1000).toISOString() : undefined,
    expiresAtIso: claims.exp ? new Date(claims.exp * 1000).toISOString() : undefined,
    rawClaims: claims as Record<string, unknown>,
  };
}

export async function copyClaimsToClipboard(
  claims: Record<string, unknown>,
): Promise<CopyClaimsResult> {
  if (!navigator.clipboard) {
    return { ok: false, reason: 'clipboard_unavailable' };
  }
  try {
    await navigator.clipboard.writeText(JSON.stringify(claims, null, 2));
    return { ok: true };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'NotAllowedError') {
      return { ok: false, reason: 'permission_denied' };
    }
    return {
      ok: false,
      reason: 'unknown',
      errorMessage: e instanceof Error ? e.message : 'Unknown error',
    };
  }
}

function formatUnix(ts: number): string {
  return new Date(ts * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function timeRemaining(exp: number): string {
  const diff = exp - Math.floor(Date.now() / 1000);
  if (diff <= 0) return 'Expired';
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return `${m}m ${s}s remaining`;
}

export default function ProfileClaimsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Get raw claims from the user object
  const rawClaims = (user?.rawClaims ?? {}) as IdTokenClaims | null;
  const vm = buildProfileClaimsViewModel(
    rawClaims && 'sub' in rawClaims ? (rawClaims as IdTokenClaims) : null,
  );

  const claims = vm.rawClaims as Record<string, unknown> & Partial<IdTokenClaims>;

  const initials = vm.displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const exp = claims.exp as number | undefined;
  const emailVerified = claims.email_verified as boolean | undefined;
  const cognitoUsername = claims['cognito:username'] as string | undefined;

  // Derive identity provider from cognito:username prefix (e.g. "Google_sub123" → "Google")
  const identityProvider = cognitoUsername?.includes('_')
    ? cognitoUsername.split('_')[0]
    : undefined;

  const expiryPct = exp
    ? Math.max(0, Math.min(100, ((exp - Math.floor(Date.now() / 1000)) / 3600) * 100))
    : 0;

  return (
    <GradientBackground>
      <NavBar
        userName={vm.displayName}
        userEmail={vm.email}
        active="profile"
        onLogout={logout}
      />

      <div style={layoutStyle}>
        {/* ---- Back button ---- */}
        <button className="anim-fade-in" onClick={() => navigate('/app')} style={backBtnStyle}>
          ← Back to Home
        </button>

        <div style={twoColStyle}>
          {/* ================================================================
              LEFT COLUMN — Identity card
          ================================================================ */}
          <div style={leftColStyle}>
            <GlassCard elevated style={profileCardStyle} className="anim-fade-in-up">
              {/* Avatar */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginBottom: '24px',
                }}
              >
                <div style={avatarLargeStyle}>
                  {vm.avatarUrl ? (
                    <img
                      data-testid="profile-identity-picture"
                      src={vm.avatarUrl}
                      alt={vm.displayName}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '32px', fontWeight: 800, color: '#fff' }}>
                      {initials}
                    </span>
                  )}
                </div>
                {!vm.avatarUrl && (
                  <div style={noPhotoChipStyle}>
                    <span style={{ fontSize: '10px' }}>👤</span>
                    No photo claim
                  </div>
                )}
              </div>

              <h2
                data-testid="profile-identity-name"
                style={{
                  fontSize: '22px',
                  fontWeight: 800,
                  color: '#fff',
                  textAlign: 'center',
                  letterSpacing: '-0.02em',
                  margin: '0 0 20px',
                }}
              >
                {vm.displayName}
              </h2>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                  marginBottom: '24px',
                }}
              >
                {emailVerified && (
                  <span
                    style={{
                      ...badgeStyle,
                      background: 'rgba(74,222,128,0.12)',
                      border: '1px solid rgba(74,222,128,0.3)',
                      color: '#4ade80',
                    }}
                  >
                    ✓ Email verified
                  </span>
                )}
                {identityProvider && (
                  <span
                    style={{
                      ...badgeStyle,
                      background: 'rgba(168,85,247,0.12)',
                      border: '1px solid rgba(168,85,247,0.3)',
                      color: '#c084fc',
                    }}
                  >
                    {identityProvider} IdP
                  </span>
                )}
                {vm.email.endsWith('@hopper.dev') && (
                  <span
                    style={{
                      ...badgeStyle,
                      background: 'rgba(59,130,246,0.12)',
                      border: '1px solid rgba(59,130,246,0.3)',
                      color: '#93c5fd',
                    }}
                  >
                    @hopper.dev
                  </span>
                )}
              </div>

              <div style={dividerStyle} />

              {exp && (
                <div style={{ marginTop: '20px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        color: 'rgba(255,255,255,0.4)',
                        fontWeight: 500,
                      }}
                    >
                      Token expiry
                    </span>
                    <span style={{ fontSize: '11px', color: '#4ade80', fontWeight: 600 }}>
                      {timeRemaining(exp)}
                    </span>
                  </div>
                  <div style={progressTrackStyle}>
                    <div
                      style={{ ...progressFillStyle, width: `${expiryPct}%`, background: '#4ade80' }}
                    />
                  </div>
                  <p
                    style={{
                      fontSize: '10px',
                      color: 'rgba(255,255,255,0.25)',
                      marginTop: '6px',
                      textAlign: 'right',
                    }}
                  >
                    Expires {formatUnix(exp)}
                  </p>
                </div>
              )}
            </GlassCard>
          </div>

          {/* ================================================================
              RIGHT COLUMN — Claims detail via ClaimsDisplay
          ================================================================ */}
          <div style={rightColStyle} className="anim-fade-in-up delay-100">
            <ClaimsDisplay
              claims={vm.rawClaims}
              fields={{
                email: vm.email || undefined,
                name: vm.displayName || undefined,
                pictureUrl: vm.avatarUrl,
                subject: vm.subject,
                issuer: vm.issuer,
                expiresAt: exp,
              }}
            />
          </div>
        </div>
      </div>
    </GradientBackground>
  );
}

/* ================================================================== */
/*  Styles                                                              */
/* ================================================================== */

const layoutStyle: CSSProperties = {
  paddingTop: '76px',
  paddingBottom: '48px',
  paddingLeft: '24px',
  paddingRight: '24px',
  maxWidth: '1000px',
  margin: '0 auto',
};

const backBtnStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.55)',
  cursor: 'pointer',
  marginBottom: '24px',
  transition: 'all 0.15s ease',
};

const twoColStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '280px 1fr',
  gap: '20px',
  alignItems: 'start',
};

const leftColStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const rightColStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const profileCardStyle: CSSProperties = {
  padding: '36px 28px 28px',
  textAlign: 'center',
};

const avatarLargeStyle: CSSProperties = {
  width: '88px',
  height: '88px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #a855f7, #6366f1, #3b82f6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 0 0 4px rgba(168,85,247,0.2), 0 8px 24px rgba(0,0,0,0.5)',
  marginBottom: '10px',
};

const noPhotoChipStyle: CSSProperties = {
  fontSize: '10px',
  color: 'rgba(255,255,255,0.3)',
  fontWeight: 600,
  letterSpacing: '0.04em',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '20px',
  padding: '3px 10px',
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  marginTop: '4px',
};

const badgeStyle: CSSProperties = {
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.03em',
  borderRadius: '20px',
  padding: '3px 10px',
  lineHeight: 1,
};

const dividerStyle: CSSProperties = {
  height: '1px',
  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
};

const progressTrackStyle: CSSProperties = {
  width: '100%',
  height: '4px',
  borderRadius: '2px',
  background: 'rgba(255,255,255,0.08)',
  overflow: 'hidden',
};

const progressFillStyle: CSSProperties = {
  height: '100%',
  borderRadius: '2px',
};
